export interface IssuedCode {
  code: string;
  issuedUntil: string; // ISO datetime string
  couponId: string;    // Firestore coupon document ID
  couponTitle: string;
  reused: boolean;
}

export interface ImageMetadata {
  url: string
  blurhash?: string
}

export interface Coupon {
  id: number
  image: string[]
  imageMetadata?: ImageMetadata[]
  title: string
  subtitle: string
  companyLogo: string
  discountPercentage: string
  price: string
  notes: string[]
  hoursLeft: string
  type: 'general' | 'local'
  liked: boolean
  category: string
  dueDate: string
  endAt?: Date // End date as Date object for countdown
  ctaType?: 'whatsapp' | 'url'
  ctaValue?: string
  tags?: string[]
  towns?: string | string[]
  priorityRank?: number
}

export interface SearchAndFilterProps {
  filterType: 'general' | 'local'
  onFilterChange: (type: 'general' | 'local') => void
  coupons: Coupon[]
  floatingFilter: 'benefits' | 'liked' | 'personal' | 'notifications'
  selectedCategories: string[]
  onCategoryChange: (category: string) => void
  searchValue: string
  onSearchValueChange: (value: string) => void
  onSearchChange: (searchQuery: string) => void
  setIsFilterModalOpen: (isFilterModalOpen: boolean) => void
  categories: string[]
  usedAdvancedFilters: boolean
  selectedAdvancedFiltersCategories: string[]
  onAdvancedFiltersCategoriesChange: (categories: string[]) => void
  onClearAdvancedFilters: () => void
  isShareButtonActive: boolean
  setIsShareButtonActive: (isShareButtonActive: boolean) => void
  checkedCouponsCount: number
  onShareClick: () => void
  onCouponSelect?: (couponId: number) => void // Callback when coupon is selected from dropdown
  onWhatsappClick?: () => void // Callback for WhatsApp share
  isViewingCoupon?: boolean // Whether the user is viewing a coupon slug
  onOpenPublishModal?: () => void // Callback to open publish coupon modal (managed by parent)
  isPublishModalOpen?: boolean // Whether the publish coupon modal is open
  hideFiltersLine?: boolean // When true, the bottom categories chip row collapses with a transition
}

export interface NotificationData {
  id: string
  icon: any // React.FC or string
  iconBg: string
  iconFill?: string
  title: string
  description: string
  time: string
  today: boolean
  read: boolean
  couponId?: string
  communityId?: number
}

export enum SortOption {
  Newest,
  PriceHighToLow,
  PriceLowToHigh,
}

export interface ValidityFilters {
  lastChance: boolean;
  lastWeek: boolean;
  longExpiry: boolean;
}

export interface FilterModalProps {
  onClose: () => void;
  categories: string[];
  validityFilters: ValidityFilters;
  onValidityFilterChange: (filterName: keyof ValidityFilters) => void;
  selectedAdvancedFiltersCategories: string[];
  onAdvancedFiltersCategoriesChange: (categories: string[]) => void;
  onReset: () => void;
  coupons: Coupon[];
  onApplyFilters: (sortOption: SortOption, minPrice: number, maxPrice: number) => void;
  onFilteredCountChange?: (count: number) => void;
  onRegisterApplyCallback?: (callback: () => void) => void;
  currentSortOption: SortOption;
  currentMinPrice: number;
  currentMaxPrice: number;
  sliderMax: number;
  availableTags: string[];
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
}

export interface CouponCardProps {
  image: string[]
  imageMetadata?: ImageMetadata[]
  title: string
  subtitle: string
  companyLogo: string
  discountPercentage: string
  price: string
  hoursLeft: string
  type: string
  liked: boolean
  onToggleLiked: () => void
  isChecked: boolean
  onCheckboxChange: (isChecked: boolean) => void
  onClick?: () => void
  endAt?: Date // End date for countdown
}

export interface CouponSlugProps {
  coupon: Coupon
  onBack: () => void
  liked: boolean
  onToggleLiked: () => void
  orgStore: string
}

export interface ToastProps {
  isVisible: boolean
  couponCount: number
}

export interface ShareModalProps {
  onClose: () => void
  checkedCouponsCount: number
  onShare: () => void
  selectedCouponIds: number[]
  selectedContacts: Set<number>
  onContactSelect: (userId: number) => void
  searchQuery: string
  onSearchChange: (query: string) => void
  isSharing: boolean
}

export interface GroupedContacts {
  [letter: string]: import('utils/models').User[]
}

export interface AcceptCouponModalProps {
  onClose: () => void
  couponCode?: string
  onContinue?: () => void
  isCodeCopied?: boolean
  onCodeCopied?: () => void
}

export interface ConfirmModalProps {
  onClose: () => void
  onConfirm?: () => void
  title: string
  subtitle: string
  confirmButtonText: string
  cancelButtonText: string
  isConfettiVisible: boolean
  isGiftIconVisible: boolean
  confirmButtonBackground?: string
  confirmButtonBorderColor?: string
  confirmButtonTextColor?: string
  cancelButtonBackground?: string
  cancelButtonBorderColor?: string
  cancelButtonTextColor?: string
}

export interface PersonalInformationProps {
  setIsConfirmModalOpen: (isOpen: boolean) => void
  setConfirmModalTitle: (title: string) => void
  setConfirmModalSubtitle: (subtitle: string) => void
  setConfirmModalConfirmButtonText: (text: string) => void
  setConfirmModalCancelButtonText: (text: string) => void
  setConfirmModalIsConfettiVisible: (visible: boolean) => void
  setConfirmModalIsGiftIconVisible: (visible: boolean) => void
  setConfirmModalConfirmButtonBackground: (bg: string) => void
  setConfirmModalConfirmButtonBorderColor: (color: string) => void
  setConfirmModalConfirmButtonTextColor: (color: string) => void
  setConfirmModalCancelButtonBackground: (bg: string) => void
  setConfirmModalCancelButtonBorderColor: (color: string) => void
  setConfirmModalCancelButtonTextColor: (color: string) => void
  setToastVisible: (visible: boolean) => void
  setToastTitle: (title: string) => void
  setToastSubtitle: (subtitle: string) => void
  setBlockedMembersCount: (count: number) => void
  customerPreferences: any
  saveCustomerPreferences: (preferences: any) => Promise<void>
  issuedCodes?: IssuedCode[]
  onViewIssuedCode?: (code: IssuedCode) => void
}

export interface BlockCouponsModalProps {
  onClose: () => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  setIsConfirmModalOpen: (isOpen: boolean) => void
  setConfirmModalTitle: (title: string) => void
  setConfirmModalSubtitle: (subtitle: string) => void
  setConfirmModalConfirmButtonText: (text: string) => void
  setConfirmModalCancelButtonText: (text: string) => void
  setConfirmModalIsConfettiVisible: (visible: boolean) => void
  setConfirmModalIsGiftIconVisible: (visible: boolean) => void
  setConfirmModalConfirmButtonBackground: (bg: string) => void
  setConfirmModalConfirmButtonBorderColor: (color: string) => void
  setConfirmModalConfirmButtonTextColor: (color: string) => void
  setConfirmModalCancelButtonBackground: (bg: string) => void
  setConfirmModalCancelButtonBorderColor: (color: string) => void
  setConfirmModalCancelButtonTextColor: (color: string) => void
  setBlockedMembersCount: (count: number) => void
}

export interface ToastState {
  isVisible: boolean
  title: string
  subtitle: string
}

export interface ConfirmModalState {
  isOpen: boolean
  title: string
  subtitle: string
  confirmButtonText: string
  cancelButtonText: string
  isConfettiVisible: boolean
  isGiftIconVisible: boolean
  confirmButtonBackground: string
  confirmButtonBorderColor: string
  confirmButtonTextColor: string
  cancelButtonBackground: string
  cancelButtonBorderColor: string
  cancelButtonTextColor: string
}

export interface ModalState {
  isFilterModalOpen: boolean
  isShareModalOpen: boolean
  isGetCouponModalOpen: boolean
  isBlockCouponsModalOpen: boolean
}